import { useSettingsContext } from '@/context/settings'
import type { BestDepositOptions } from '@/queries/bestDepositOptions.query'
import type { VaultApyRange } from '@/types/apy'
import type { Vault } from '@/types/vault'
import { getApyValue } from '@/utils/apy'
import { isVaultTokenInTokens } from '@/utils/vaultsList/isVaultTokenInTokens'
import { BTC_TOKENS, ETH_TOKENS, EURO_COINS, STABLE_COINS } from '@vaultsfyi/common/constants'
import type { Network } from '@vaultsfyi/common/types'
import { useMemo } from 'react'

interface Props {
  vaults: Vault[]
  depositOptions: BestDepositOptions
  apyRange: VaultApyRange
  numberOfOptions?: number
}

type VaultMapKey = `${Network}:${string}`

export const useRelevantDepositOptions = ({ vaults, depositOptions, apyRange, numberOfOptions = 5 }: Props) => {
  const { includeRewards } = useSettingsContext()
  const allowedAssets = [...ETH_TOKENS, ...STABLE_COINS, ...EURO_COINS, ...BTC_TOKENS]

  return useMemo(() => {
    const filteredVaults = vaults.filter((vault) => isVaultTokenInTokens(vault, allowedAssets))
    const vaultsMap = getVaultsMap(
      [...filteredVaults].sort(
        (a, b) =>
          getApyValue({ apy: b.apy, apyRange, includeRewards, apyComposite: b.apyComposite }) -
          getApyValue({ apy: a.apy, apyRange, includeRewards, apyComposite: a.apyComposite })
      )
    )

    const depositVaults =
      depositOptions
        ?.map(({ network, vaultId }) => vaultsMap.get(getVaultMapKey(network.name, vaultId)))
        .filter((vault): vault is Vault[][number] => vault !== undefined) || []

    const uniqueDepositOptions = Array.from(new Map([...getVaultsMap(depositVaults), ...vaultsMap]).values())

    const distinctProtocolsDepositOptions = uniqueDepositOptions.filter(
      (vault, index, self) => self.findIndex((v) => v.protocol === vault.protocol) === index
    )

    return distinctProtocolsDepositOptions.slice(0, numberOfOptions)
  }, [vaults, depositOptions, apyRange, includeRewards])
}

function getVaultMapKey(network: Network, vaultId: string): VaultMapKey {
  return `${network}:${vaultId}`
}

function getVaultsMap(vaults: Vault[]) {
  return new Map<VaultMapKey, Vault[][number]>(
    vaults.map((vault) => [getVaultMapKey(vault.network.name, vault.vaultId), vault])
  )
}
